import React, { useEffect, useLayoutEffect, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LAYER } from 'modules/Common/utils/layers';
import { anchorSelector } from 'modules/Common/utils/gapAnchor';
import { revealInScroller, scrollerOf } from 'modules/Common/utils/revealInScroller';

const FADE_MS = 160;
const PADDING = 6;
const GROUP_PADDING = 3;
const SETTLE_FRAMES = 4;
const SETTLE_MAX_MS = 1500;
const MAX_REVEALS = 3;
const REVEAL_BUDGET_MS = 4000;
const DRIFT_WINDOW_MS = 2000;
const PRIMARY_SHARE = 0.7;
const MAX_TARGETS = 16;
const COLLECT_FRAMES = 3;
const CORNER = 10;
const NOTE_GAP = 10;
const NOTE_HEIGHT = 36;
const NOTE_MAX_WIDTH = 320;
const EDGE = 12;
const LOST_GRACE_MS = 400;
const RESOLVE_GAP_MS = 120;
const REMOUNT_WAIT_MS = 1500;
const MAX_REPLACEMENTS = 3;
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [contenteditable="true"], [tabindex]:not([tabindex="-1"])';
const IDLE_KEYS = new Set(['Tab', 'Shift', 'Control', 'Alt', 'Meta']);
const GROUP = '[data-gap-group]';
const PART = '[data-gap-part]';
const CLUSTER = '[data-gap-cluster]';
const LEAF_FIELDS =
  'input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"]), textarea, select, [contenteditable]:not([contenteditable="false"])';

const listeners = new Set();
let snapshot = null;
let active = null;
let counter = 0;

const publish = (next) => {
  snapshot = next;
  listeners.forEach((listener) => listener());
};
const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};
const getSnapshot = () => snapshot;
const getServerSnapshot = () => null;

const reducedMotion = () =>
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

const laidOut = (el) => {
  const rect = el.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
};

const visibleRect = (el) => {
  const rect = el.getBoundingClientRect();
  return { top: rect.top, left: rect.left, width: rect.width, height: rect.height };
};

const sameRect = (a, b) =>
  a.pad === b.pad &&
  Math.abs(a.top - b.top) < 0.5 &&
  Math.abs(a.left - b.left) < 0.5 &&
  Math.abs(a.width - b.width) < 0.5 &&
  Math.abs(a.height - b.height) < 0.5;

const unionRect = (els, pad) => {
  const rects = els.map((el) => el.getBoundingClientRect());
  const top = Math.min(...rects.map((rect) => rect.top));
  const left = Math.min(...rects.map((rect) => rect.left));
  const bottom = Math.max(...rects.map((rect) => rect.bottom));
  const right = Math.max(...rects.map((rect) => rect.right));
  return { top, left, width: right - left, height: bottom - top, pad };
};

function layout(targets, focusEl) {
  const slots = new Map();
  const slotOf = targets.map((target) => {
    const group = target.closest(GROUP);
    const cluster = (group ?? target).closest(CLUSTER);
    const key = cluster ?? group ?? target;
    if (!slots.has(key)) {
      slots.set(key, {
        index: slots.size,
        els: new Set(),
        groups: new Set(),
        group: Boolean(cluster ?? group),
        empty: false,
      });
    }
    const slot = slots.get(key);
    if (group && !slot.groups.has(group)) {
      slot.groups.add(group);
      group.querySelectorAll(PART).forEach((part) => {
        if (laidOut(part)) slot.els.add(part);
      });
    }
    slot.els.add(target);
    if (isEmptyLeaf(target)) slot.empty = true;
    return slot.index;
  });
  const focus = targets.indexOf(focusEl);
  const focusSlot = focus === -1 ? -1 : slotOf[focus];
  const all = [...slots.values()];
  const kept = all.some((slot) => slot.empty)
    ? all.filter((slot) => slot.empty || slot.index === focusSlot)
    : all;
  const rects = kept.map((slot) => unionRect([...slot.els], slot.group ? GROUP_PADDING : PADDING));
  return { rects, focusIndex: kept.findIndex((slot) => slot.index === focusSlot) };
}

const asList = (value) => (Array.isArray(value) ? value : [value]).filter(Boolean);

export function isEmptyLeaf(el) {
  if (!el?.getAttribute) return false;
  const flag = el.getAttribute('data-gap-empty');
  if (flag === 'true') return true;
  if (flag === 'false') return false;
  if (el.matches('input[type="checkbox"]')) return !el.checked;
  if (el.matches('[role="radiogroup"], [role="group"]'))
    return !el.querySelector('input:checked, [aria-checked="true"]');
  if (el.matches('input, textarea, select')) return String(el.value ?? '').trim() === '';
  if (el.matches('[contenteditable]:not([contenteditable="false"])'))
    return (el.textContent ?? '').trim() === '';
  const inner = el.querySelectorAll(LEAF_FIELDS);
  return inner.length === 1 ? isEmptyLeaf(inner[0]) : false;
}

const documentOrder = (a, b) =>
  a === b ? 0 : a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;

const matchesOf = (anchors) =>
  anchors.flatMap((anchor) => [...document.querySelectorAll(anchorSelector(anchor))]);

function findTargets(anchors, within) {
  const scopes = within ? matchesOf(asList(within)) : null;
  if (scopes && scopes.length === 0) return [];
  const found = [];
  for (const el of matchesOf(anchors)) {
    if (found.includes(el) || !laidOut(el)) continue;
    if (scopes && !scopes.some((scope) => scope.contains(el))) continue;
    found.push(el);
  }
  return found.sort(documentOrder).slice(0, MAX_TARGETS);
}

function choose(list, pick) {
  if (pick !== 'firstEmpty' && pick !== 'emptyOnly') return list;
  const empty = list.find(isEmptyLeaf);
  if (empty) return [empty];
  return pick === 'emptyOnly' ? [] : list.slice(0, 1);
}

function createSession() {
  counter += 1;
  const session = {
    id: counter,
    cancelled: false,
    path: window.location.pathname,
    cleanups: new Set(),
    onCancel(fn) {
      session.cleanups.add(fn);
      return () => session.cleanups.delete(fn);
    },
    cancel() {
      if (session.cancelled) return;
      session.cancelled = true;
      const pending = [...session.cleanups];
      session.cleanups.clear();
      pending.forEach((fn) => fn());
    },
  };
  return session;
}

function waitFor(anchors, within, ms, session) {
  return new Promise((resolve) => {
    if (session.cancelled) {
      resolve([]);
      return;
    }
    const immediate = findTargets(anchors, within);
    if (immediate.length || ms <= 0) {
      resolve(immediate);
      return;
    }
    let done = false;
    let frame = 0;
    let timer = 0;
    let off = () => {};
    const mutations = new MutationObserver(() => schedule());
    const resizes = new ResizeObserver(() => schedule());
    const finish = (found) => {
      if (done) return;
      done = true;
      mutations.disconnect();
      resizes.disconnect();
      clearTimeout(timer);
      cancelAnimationFrame(frame);
      off();
      resolve(found);
    };
    const check = () => {
      frame = 0;
      if (done) return;
      const found = findTargets(anchors, within);
      if (found.length) {
        finish(found);
        return;
      }
      matchesOf(anchors).forEach((pending) => {
        resizes.observe(pending);
      });
    };
    function schedule() {
      if (!frame && !done) frame = requestAnimationFrame(check);
    }
    mutations.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
    });
    timer = setTimeout(() => finish([]), ms);
    off = session.onCancel(() => finish([]));
    schedule();
  });
}

function collect(anchors, within, first, session) {
  return new Promise((resolve) => {
    let frames = 0;
    let frame = 0;
    let off = () => {};
    const finish = (found) => {
      cancelAnimationFrame(frame);
      off();
      resolve(found);
    };
    const tick = () => {
      frames += 1;
      if (frames < COLLECT_FRAMES) {
        frame = requestAnimationFrame(tick);
        return;
      }
      const found = findTargets(anchors, within);
      finish(found.length ? found : first);
    };
    off = session.onCancel(() => finish([]));
    frame = requestAnimationFrame(tick);
  });
}

async function gather(anchors, within, pick, ms, session) {
  const first = await waitFor(anchors, within, ms, session);
  if (!first.length || session.cancelled) return [];
  return choose(await collect(anchors, within, first, session), pick);
}

function blockFor(el) {
  return el.getBoundingClientRect().height > window.innerHeight * 0.8 ? 'start' : 'center';
}

function inside(rect, frame, tall) {
  return (
    rect.top >= frame.top - 1 &&
    (tall ? rect.top < frame.bottom - 1 : rect.bottom <= frame.bottom + 1) &&
    rect.right > frame.left &&
    rect.left < frame.right
  );
}

function reachable(el) {
  const rect = el.getBoundingClientRect();
  const tall = rect.height > window.innerHeight * 0.8;
  const viewport = { top: 0, left: 0, right: window.innerWidth, bottom: window.innerHeight };
  if (!inside(rect, viewport, tall)) return false;
  const scroller = scrollerOf(el);
  return !scroller || inside(rect, scroller.getBoundingClientRect(), tall);
}

function reveal(el, behavior, { native = false } = {}) {
  const block = blockFor(el);
  revealInScroller(el, { block, behavior });
  if (!native) return;
  el.scrollIntoView?.({
    block,
    inline: 'nearest',
    behavior: behavior ?? (reducedMotion() ? 'auto' : 'smooth'),
  });
}

function settle(el, session) {
  return new Promise((resolve) => {
    const started = performance.now();
    let last = null;
    let steady = 0;
    let frame = 0;
    let off = () => {};
    const finish = () => {
      cancelAnimationFrame(frame);
      off();
      resolve();
    };
    const tick = () => {
      if (session.cancelled || !el.isConnected) {
        finish();
        return;
      }
      const rect = visibleRect(el);
      steady = last && sameRect(rect, last) ? steady + 1 : 0;
      last = rect;
      if (steady >= SETTLE_FRAMES || performance.now() - started > SETTLE_MAX_MS) {
        finish();
        return;
      }
      frame = requestAnimationFrame(tick);
    };
    off = session.onCancel(finish);
    frame = requestAnimationFrame(tick);
  });
}

async function bringIntoView(el, session) {
  reveal(el, null);
  await settle(el, session);
  const deadline = performance.now() + REVEAL_BUDGET_MS;
  for (
    let attempt = 0;
    attempt < MAX_REVEALS &&
    !session.cancelled &&
    el.isConnected &&
    performance.now() < deadline &&
    !reachable(el);
    attempt += 1
  ) {
    reveal(el, 'auto', { native: true });
    await settle(el, session);
  }
}

function focusInside(el) {
  const candidate = el.matches(FOCUSABLE) ? el : el.querySelector(FOCUSABLE);
  if (candidate && laidOut(candidate)) candidate.focus({ preventScroll: true });
}

function removeNow(session) {
  if (snapshot && snapshot.id === session.id) publish(null);
}

function endSession(session, { immediate = false } = {}) {
  if (active === session) active = null;
  session.cancel();
  if (!snapshot || snapshot.id !== session.id) return;
  if (immediate || reducedMotion()) {
    removeNow(session);
    return;
  }
  publish({ ...snapshot, leaving: true });
  setTimeout(() => removeNow(session), FADE_MS);
}

export function dismissSpotlight({ immediate = false } = {}) {
  if (active) endSession(active, { immediate });
}

const sameRects = (a, b) => a.length === b.length && a.every((rect, i) => sameRect(rect, b[i]));

function present(session, initial, focusEl, outcome, notes, scope) {
  let targets = initial;
  let onFallback = outcome === 'fallback';
  const firstNote = onFallback ? (notes.fallbackNote ?? notes.note) : notes.note;
  const first = layout(targets, focusEl);
  publish({
    id: session.id,
    rects: first.rects,
    focusIndex: first.focusIndex,
    note: firstNote,
    defaultNote: onFallback && firstNote === null,
    leaving: false,
  });

  const dismiss = () => endSession(session);
  const hits = (event) =>
    event.target instanceof Node && targets.some((target) => target.contains(event.target));
  const onPointerDown = () => dismiss();
  const onHidden = () => {
    if (document.hidden) dismiss();
  };
  const onWindowBlur = () => dismiss();
  const onEscape = (event) => {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    event.stopPropagation();
    dismiss();
  };
  const onKeyDown = (event) => {
    if (IDLE_KEYS.has(event.key)) return;
    if (hits(event)) dismiss();
  };
  const onInput = (event) => {
    if (hits(event)) dismiss();
  };
  document.addEventListener('pointerdown', onPointerDown, true);
  window.addEventListener('keydown', onEscape, true);
  document.addEventListener('keydown', onKeyDown, true);
  document.addEventListener('input', onInput, true);
  document.addEventListener('visibilitychange', onHidden);
  window.addEventListener('blur', onWindowBlur);

  let frame = 0;
  const presentedAt = performance.now();
  let drifted = false;
  let focusHeld = true;
  let lostSince = 0;
  let lastResolve = 0;
  let dirty = false;
  const mutations = new MutationObserver(() => {
    dirty = true;
  });
  mutations.observe(document.documentElement, { childList: true, subtree: true });

  const heldNow = () => {
    const current = document.activeElement;
    return (
      current instanceof Element &&
      current !== document.body &&
      targets.some((target) => target.contains(current))
    );
  };

  const focusParked = (el) => {
    const current = document.activeElement;
    const onBody = !current || current === document.body || !current.isConnected;
    return onBody && !el.matches(FOCUSABLE) && !el.querySelector(FOCUSABLE);
  };

  const resolve = () => {
    const exact = choose(findTargets(scope.anchors, scope.within), scope.pick);
    if (exact.length) return { list: exact, exact: true };
    if (onFallback) return { list: findTargets(scope.fallbacks, null), exact: false };
    return { list: [], exact: false };
  };

  const sameList = (a, b) => a.length === b.length && a.every((el, i) => el === b[i]);

  const adopt = (found) => {
    const previous = focusEl;
    targets = found.list;
    focusEl = targets.find(isEmptyLeaf) ?? targets[0];
    if (found.exact && onFallback) {
      onFallback = false;
      if (snapshot && snapshot.id === session.id)
        publish({ ...snapshot, note: notes.note, defaultNote: false });
    }
    if (focusEl !== previous && !reachable(focusEl)) reveal(focusEl, 'auto', { native: true });
    const current = document.activeElement;
    const focusLost = !current || current === document.body || !current.isConnected;
    if (focusHeld && (focusEl !== previous || focusLost)) focusInside(focusEl);
  };

  const follow = () => {
    if (session.cancelled) return;
    const intact = targets.every((target) => target.isConnected && laidOut(target));
    if (intact) focusHeld = heldNow() || (focusHeld && focusParked(focusEl));
    const now = performance.now();
    if (!intact || (dirty && now - lastResolve >= RESOLVE_GAP_MS)) {
      dirty = false;
      lastResolve = now;
      const found = resolve();
      if (found.list.length === 0) {
        if (!lostSince) lostSince = performance.now();
        if (performance.now() - lostSince > LOST_GRACE_MS) {
          dismiss();
          return;
        }
        frame = requestAnimationFrame(follow);
        return;
      }
      lostSince = 0;
      if (!sameList(found.list, targets)) adopt(found);
    } else {
      lostSince = 0;
    }
    const focusTarget = targets.includes(focusEl) ? focusEl : targets[0];
    const { rects, focusIndex } = layout(targets, focusTarget);
    const focusRect = rects[focusIndex];
    if (
      !drifted &&
      performance.now() - presentedAt < DRIFT_WINDOW_MS &&
      (focusRect.top + focusRect.height <= 0 || focusRect.top >= window.innerHeight)
    ) {
      drifted = true;
      reveal(focusTarget, 'auto', { native: true });
    }
    if (
      snapshot &&
      snapshot.id === session.id &&
      !snapshot.leaving &&
      (!sameRects(snapshot.rects, rects) || snapshot.focusIndex !== focusIndex)
    )
      publish({ ...snapshot, rects, focusIndex });
    frame = requestAnimationFrame(follow);
  };
  frame = requestAnimationFrame(follow);

  session.onCancel(() => {
    cancelAnimationFrame(frame);
    mutations.disconnect();
    document.removeEventListener('pointerdown', onPointerDown, true);
    window.removeEventListener('keydown', onEscape, true);
    document.removeEventListener('keydown', onKeyDown, true);
    document.removeEventListener('input', onInput, true);
    document.removeEventListener('visibilitychange', onHidden);
    window.removeEventListener('blur', onWindowBlur);
  });

  focusInside(focusEl);
  focusHeld = heldNow() || focusParked(focusEl);
}

async function run(session, { anchors, fallbacks, note, fallbackNote, timeoutMs, pick, within }) {
  const started = performance.now();
  let outcome = 'exact';
  let targets = await gather(
    anchors,
    within,
    pick,
    fallbacks.length ? timeoutMs * PRIMARY_SHARE : timeoutMs,
    session,
  );
  if (!targets.length && !session.cancelled && fallbacks.length) {
    outcome = 'fallback';
    targets = await gather(
      fallbacks,
      null,
      'all',
      Math.max(timeoutMs - (performance.now() - started), 0),
      session,
    );
  }
  if (!targets.length || session.cancelled) {
    if (active === session) active = null;
    session.cancel();
    return 'none';
  }
  let focusEl = targets.find(isEmptyLeaf) ?? targets[0];
  let live = targets;
  for (let round = 0; round <= MAX_REPLACEMENTS; round += 1) {
    await bringIntoView(focusEl, session);
    if (session.cancelled) return 'none';
    live = targets.filter((target) => target.isConnected && laidOut(target));
    if (live.length === targets.length) break;
    const replaced = await gather(
      outcome === 'fallback' ? fallbacks : anchors,
      outcome === 'fallback' ? null : within,
      outcome === 'fallback' ? 'all' : pick,
      live.length === 0 ? REMOUNT_WAIT_MS : 0,
      session,
    );
    if (session.cancelled) return 'none';
    if (replaced.length === 0) break;
    targets = replaced;
    focusEl = targets.find(isEmptyLeaf) ?? targets[0];
    live = targets;
  }
  if (live.length === 0) {
    endSession(session, { immediate: true });
    return 'none';
  }
  present(
    session,
    live,
    live.includes(focusEl) ? focusEl : live[0],
    outcome,
    { note, fallbackNote },
    {
      anchors,
      fallbacks,
      within,
      pick,
    },
  );
  return outcome;
}

export function spotlight({
  anchor,
  fallback = null,
  note = null,
  fallbackNote = null,
  timeoutMs = 6000,
  pick = 'firstEmpty',
  within = null,
  signal = null,
} = {}) {
  if (signal?.aborted) return Promise.resolve('none');
  dismissSpotlight({ immediate: true });
  const anchors = asList(anchor ?? []);
  if (!anchors.length || typeof document === 'undefined') return Promise.resolve('none');
  const session = createSession();
  active = session;
  if (signal) {
    const stop = () => endSession(session, { immediate: true });
    signal.addEventListener('abort', stop);
    session.onCancel(() => signal.removeEventListener('abort', stop));
  }
  return run(session, {
    anchors,
    fallbacks: asList(fallback ?? []),
    note,
    fallbackNote,
    timeoutMs,
    pick,
    within,
  });
}

function notePosition(rect) {
  const pad = rect.pad ?? PADDING;
  const below = rect.top + rect.height + pad + NOTE_GAP;
  const placeBelow = below + NOTE_HEIGHT <= window.innerHeight - EDGE;
  const top = placeBelow ? below : Math.max(EDGE, rect.top - pad - NOTE_GAP - NOTE_HEIGHT);
  const left = Math.min(
    Math.max(EDGE, rect.left - pad),
    Math.max(EDGE, window.innerWidth - NOTE_MAX_WIDTH - EDGE),
  );
  return { top, left };
}

export function CgSpotlightHost() {
  const { t } = useTranslation('common');
  const { pathname } = useLocation();
  const current = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    if (active && active.path !== pathname) dismissSpotlight({ immediate: true });
  }, [pathname]);

  useEffect(() => dismissSpotlight, []);

  const currentId = current?.id ?? null;

  useLayoutEffect(() => {
    if (currentId === null) {
      setEntered(false);
      return undefined;
    }
    if (reducedMotion()) {
      setEntered(true);
      return undefined;
    }
    setEntered(false);
    const frame = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(frame);
  }, [currentId]);

  if (!current) return null;

  const { rects, focusIndex } = current;
  const rect = rects[focusIndex] ?? rects[0];
  const visible = entered && !current.leaving;
  const noteText = current.defaultNote
    ? t('spotlight.fallbackNote', 'We could not find the exact item, so this is where it lives.')
    : current.note;
  const position = noteText ? notePosition(rect) : null;
  const animated = !reducedMotion();
  const maskId = `cg-spotlight-mask-${current.id}`;

  return createPortal(
    <div
      aria-hidden="true"
      data-testid="cg-spotlight"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: LAYER.spotlight,
        pointerEvents: 'none',
        overflow: 'hidden',
        opacity: visible ? 1 : 0,
        transition: animated ? `opacity ${FADE_MS}ms ease-out` : 'none',
      }}
    >
      <svg
        width="100%"
        height="100%"
        style={{ position: 'absolute', inset: 0, display: 'block', pointerEvents: 'none' }}
      >
        <defs>
          <mask id={maskId}>
            <rect
              x="0"
              y="0"
              width="100%"
              height="100%"
              fill="white"
            />
            {rects.map((cut, index) => (
              <rect
                key={index}
                x={cut.left - cut.pad}
                y={cut.top - cut.pad}
                width={cut.width + cut.pad * 2}
                height={cut.height + cut.pad * 2}
                rx={CORNER}
                fill="black"
              />
            ))}
          </mask>
        </defs>
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          className="fill-bsGray-900"
          fillOpacity="0.55"
          mask={`url(#${maskId})`}
        />
        {rects.map((cut, index) => (
          <rect
            key={index}
            data-testid="cg-spotlight-cutout"
            x={cut.left - cut.pad - 1}
            y={cut.top - cut.pad - 1}
            width={cut.width + cut.pad * 2 + 2}
            height={cut.height + cut.pad * 2 + 2}
            rx={CORNER + 1}
            fill="none"
            strokeWidth="2"
            style={{ stroke: 'rgb(var(--primary-color))' }}
          />
        ))}
      </svg>
      {noteText ? (
        <div
          data-testid="cg-spotlight-note"
          className="bg-white text-bsGray-600"
          style={{
            position: 'absolute',
            top: position.top,
            left: position.left,
            maxWidth: NOTE_MAX_WIDTH,
            pointerEvents: 'none',
            borderRadius: 8,
            fontSize: 13,
            lineHeight: '20px',
            padding: '6px 10px',
            boxShadow: '0 1px 3px rgba(15, 23, 42, 0.25)',
          }}
        >
          {noteText}
        </div>
      ) : null}
    </div>,
    document.body,
  );
}

export default CgSpotlightHost;
